import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { registerRequest } from '../../lib/api/auth';
import {
  AuthError,
  AuthField,
  AuthShell,
  AuthSubmit,
  PasswordStrengthMeter
} from './components/AuthForm';

/**
 * Create an account — REQUEST-ONLY by design (auth-full-spec §1). Submitting
 * NEVER creates a PocketBase user: it files a client_requests row through the
 * backend's public /api/clients/public-requests endpoint so the agency sees it
 * in the Cockpit triage feed and provisions access out of band (§7). No SSO
 * here either — Google sign-in lives on /login only (§0 audit decision).
 */
function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', organization: '', password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const update = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await registerRequest({
        email: form.email,
        password: form.password,
        name: form.name,
        tenant: form.organization
      });
      // Request-only: the agency provisions access out of band (§7). No
      // session is established and none is expected — applying a token here
      // (the pre-fix trap) landed users inside the app where every
      // authenticated call 401'd.
      setNotice('Account request received — the agency will provision your access.');
      setForm({ name: '', email: '', organization: '', password: '', confirm: '' });
    } catch (requestError) {
      setError(requestError.message || 'Unable to submit your request right now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="NEW TENANT"
      subtitle="Request a client account — this is a request, not instant signup. Your agency operators will provision and onboard your agents."
      title="Create account"
    >
      <form onSubmit={handleSubmit}>
        <AuthField
          autoComplete="name"
          id="register-name"
          label="Full name"
          onChange={update('name')}
          placeholder="Alex Rivera"
          value={form.name}
        />
        <AuthField
          autoComplete="email"
          id="register-email"
          label="Work email"
          onChange={update('email')}
          placeholder="you@company.com"
          type="email"
          value={form.email}
        />
        <AuthField
          autoComplete="organization"
          hint="Shown as your portal's white-label brand."
          id="register-org"
          label="Organization"
          onChange={update('organization')}
          placeholder="Acme Corporation"
          value={form.organization}
        />
        <AuthField
          autoComplete="new-password"
          id="register-password"
          label="Password"
          onChange={update('password')}
          type="password"
          value={form.password}
        />
        <PasswordStrengthMeter password={form.password} />
        <AuthField
          autoComplete="new-password"
          id="register-confirm"
          label="Confirm password"
          onChange={update('confirm')}
          type="password"
          value={form.confirm}
        />
        {notice && (
          <div className="auth-notice" role="status">{notice}</div>
        )}
        <AuthError message={error} />
        <AuthSubmit busy={busy}>Request account</AuthSubmit>
      </form>

      <div className="auth-links">
        <span>Already have an account?</span>
        <Link to="/login">Sign in</Link>
      </div>
    </AuthShell>
  );
}

export default RegisterPage;
